import { resolveCapabilityModeIcon } from '../capabilities/capabilityIcons.jsx';

export const MODE_KEYS = {
  auto: 'auto',
  expert: 'expert',
  intake: 'requirement_intake',
  search: 'intelligent_sourcing',
  analysis: 'analysis_mode',
  domainFlow: 'domain_flow',
};

export const MESSAGE_ATTACHMENT_MAX_COUNT = 10;
export const MESSAGE_ATTACHMENT_MAX_SIZE_MB = 20;
export const MESSAGE_ATTACHMENT_ACCEPT_ATTR = '.pdf,.docx,.xlsx,.csv,.txt,.md,.png,.jpg,.jpeg,.webp';
export const MESSAGE_ATTACHMENT_CONSTRAINTS_HINT = `当前附件仅用于本次消息。最多 ${MESSAGE_ATTACHMENT_MAX_COUNT} 个，单个不超过 ${MESSAGE_ATTACHMENT_MAX_SIZE_MB}MB。`;

const QUICK_MODE_KEY_ALIASES = Object.freeze({
  requirement_intake: MODE_KEYS.intake,
  intelligent_sourcing: MODE_KEYS.search,
  sourcing: MODE_KEYS.search,
  search: MODE_KEYS.search,
  analysis_mode: MODE_KEYS.analysis,
  analysis: MODE_KEYS.analysis,
  domain_flow: MODE_KEYS.domainFlow,
});

export function normalizeQuickModeKey(modeKey) {
  const normalizedModeKey = String(modeKey || '').trim();
  return QUICK_MODE_KEY_ALIASES[normalizedModeKey] || normalizedModeKey;
}

export function isSendTriggerEnabled({
  hasComposerChooser = false,
  disabled = false,
  loading = false,
  submitting = false,
  sendDisabled = false,
  event = null,
}) {
  const nativeEvent = Object.assign({}, event?.nativeEvent);
  return [
    hasComposerChooser,
    disabled,
    loading,
    submitting,
    sendDisabled,
    nativeEvent.isComposing === true,
  ].every((blocked) => !blocked);
}

export function resolveSendDisabledState({
  disabled = false,
  loading = false,
  submitting = false,
  sendDisabled = false,
}) {
  return Boolean(disabled || loading || submitting || sendDisabled);
}

const ATTACHMENT_STATUS_LABELS = Object.freeze({
  uploading: '上传中',
  uploaded: '已上传，解析中',
  processing: '解析中',
  parsing: '解析中',
  chunked: '切分中',
  embedded: '向量化中',
  indexed: '索引中',
  failed: '失败',
  error: '失败',
});

export function getAttachmentFailureReason(file) {
  return String(file?.error_message || file?.error || '').trim();
}

function resolveAttachmentStatusLabel(status) {
  const normalizedStatus = String(status || '').trim().toLowerCase();
  if (normalizedStatus.startsWith('failed')) {
    return '失败';
  }
  return ATTACHMENT_STATUS_LABELS[normalizedStatus] || '';
}

function formatAttachmentFailureReason(reason) {
  const normalizedReason = String(reason || '').trim();
  const maxReasonChars = 18;
  const reasonChars = Array.from(normalizedReason);
  return reasonChars.length > maxReasonChars
    ? `${reasonChars.slice(0, maxReasonChars - 1).join('')}…`
    : normalizedReason;
}

function truncateAttachmentName(rawName) {
  const maxNameChars = 10;
  const nameChars = Array.from(rawName);
  return nameChars.length > maxNameChars
    ? `${nameChars.slice(0, maxNameChars - 1).join('')}…`
    : rawName;
}

function formatAttachmentStatusText(baseName, statusLabel, file) {
  if (statusLabel !== '失败') {
    return statusLabel ? `${baseName} · ${statusLabel}` : baseName;
  }
  const failureReason = formatAttachmentFailureReason(getAttachmentFailureReason(file));
  return failureReason ? `${baseName} · ${statusLabel}：${failureReason}` : `${baseName} · ${statusLabel}`;
}

export function getAttachmentDisplayText(file) {
  const record = Object.assign({}, file);
  const normalizedStatus = String(record.status ?? '').trim().toLowerCase();
  const rawName = String(record.name ?? '').trim() || '未命名附件';
  const hasSourceId = Boolean(String([record.source_id, record.id].find(Boolean) ?? '').trim());
  const baseName = truncateAttachmentName(rawName);

  const isReady = normalizedStatus === 'ready';
  if ([isReady, hasSourceId].every(Boolean)) {
    return baseName;
  }
  if ([isReady, !hasSourceId].every(Boolean)) {
    return `${baseName} · 等待上传`;
  }
  const statusLabel = resolveAttachmentStatusLabel(normalizedStatus);
  return formatAttachmentStatusText(baseName, statusLabel, file);
}

export function getAttachmentTitleText(file) {
  const record = Object.assign({}, file);
  const rawName = String([record.name, record.filename].find(Boolean) ?? '').trim() || '未命名附件';
  const failureReason = getAttachmentFailureReason(file);
  return failureReason ? `${rawName}：${failureReason}` : rawName;
}

export function resolveQuickModeIcon(modeKey) {
  const normalizedModeKey = normalizeQuickModeKey(modeKey);
  return resolveCapabilityModeIcon(normalizedModeKey);
}

const resolveQuickModeKind = (entry) => (
  entry?.kind === 'mode' ? 'mode' : 'skill'
);

export function normalizeQuickModeEntries(modeOptions = []) {
  const candidates = Array.isArray(modeOptions) ? modeOptions : [];

  const normalizedEntries = candidates
    .filter((entry) => {
      const normalizedModeKey = normalizeQuickModeKey(entry?.value);
      return Boolean(
        normalizedModeKey
        && normalizedModeKey !== MODE_KEYS.auto
      );
    })
    .map((entry) => ({
      key: String(entry.value).trim(),
      label: String(entry?.label || entry?.value || '').trim(),
      ...(String(entry?.description || '').trim()
        ? { description: String(entry.description).trim() }
        : {}),
      kind: resolveQuickModeKind(entry),
    }));

  return normalizedEntries;
}

export function resolveQuickModeStateText(modeStatusByKey = {}, modeKey) {
  const rawStatus = String(modeStatusByKey?.[modeKey] || '').trim().toLowerCase();
  const stateTextByStatus = new Map([
    ['starting', '启动中'],
    ['pending', '启动中'],
    ['active', ''],
    ['running', ''],
    ['waiting_for_user', '等待确认'],
    ['paused', '已暂停'],
  ]);
  return stateTextByStatus.has(rawStatus)
    ? stateTextByStatus.get(rawStatus)
    : '';
}
